import { useAction, useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { AttentionIcon } from '@blade/ui-kit'
import {
  PairSearchItem,
  PairSearchItemSkeleton,
} from '@root/entities/pair/ui/pair-search-item'
import { addPairToRecentSearchesAction } from '@root/features/user/recent-searches'
import { useTranslation } from '@root/shared/localization'
import { TradingList } from '@root/shared/trading'
import { InfoMessage } from '@root/shared/ui-kit'

import { SearchModalRecommended } from './search-modal-recommended'

import type { Pair, PairModel } from '@root/entities/pair'
import type { SearchPairModel } from '@root/features/services/search-pair'

type SearchModalResultsProps = {
  model: SearchPairModel
  onPairSelected?: (pair: Pair.PairMinimal) => void
  onClose?: () => void
}

export const SearchModalPairsResults: React.FC<SearchModalResultsProps> = ({
  model,
  onPairSelected,
  onClose,
}) => {
  const { t } = useTranslation()
  const addPairToRecentSearches = useAction(addPairToRecentSearchesAction)
  const [searchState] = useAtom(model.searchStateAtom)
  const searchTokenAction = useAction(model.search)

  const handlePairSelected = (pair: Pair.PairMinimal) => {
    onPairSelected?.(pair)
    addPairToRecentSearches(pair)
  }

  const renderContent = () => {
    if (AsyncStateN.isNotInitiated(searchState)) {
      return (
        <SearchModalRecommended
          onPairSelected={handlePairSelected}
          onClose={onClose}
        />
      )
    }

    if (AsyncStateN.isError(searchState)) {
      return (
        <InfoMessage
          intent="error"
          variant="dt"
          icon={<AttentionIcon />}
          title={t('common.loading-error')}
          description={t('common.something-went-wrong')}
          onRetry={() => searchTokenAction()}
          withSeparator
        />
      )
    }

    return (
      <TradingList<PairModel>
        getKey={(it) => it.address}
        renderItem={(it) => (
          <PairSearchItem
            pair={it}
            onClick={handlePairSelected}
            onLinkClick={() => {
              addPairToRecentSearches(it)
              onClose?.()
            }}
          />
        )}
        renderSkeleton={() => <PairSearchItemSkeleton />}
        emptyTitle={t('common.unfortunately')}
        emptyText={t('common.not-a-single-match-was-found')}
        state={searchState}
        skeletonsCount={5}
        css={css`
          padding-inline-start: 3px;
        `}
      />
    )
  }

  return <Root>{renderContent()}</Root>
}

const Root = styled.div`
  grid-row: results;
  display: grid;
  min-height: 0;
  overflow: hidden;
`
